import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, CalendarDays, Clock3 } from "lucide-react";
import Navbar from "../components/marketing/Navbar";
import Footer from "../components/marketing/Footer";
import { Container, Reveal, Eyebrow } from "../components/marketing/common";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (d) =>
    new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

const Meta = ({ post }) => (
    <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-medium text-[#94A3B8]">
        <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />{fmt(post.published_at)}</span>
        <span className="flex items-center gap-1"><Clock3 className="h-3 w-3" />{post.read_minutes} min read</span>
    </span>
);

export default function Blog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Blog | Kryvo CRM — CRM Guides for Growing Businesses";
        axios.get(`${API}/blog/posts`)
            .then((r) => setPosts(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const [featured, ...rest] = posts;

    return (
        <div className="min-h-screen bg-white">
            <Navbar />
            <main className="pt-[68px]">
                <section className="bg-gradient-to-b from-[#F7FEF9] to-white py-16 lg:py-20">
                    <Container>
                        <Reveal>
                            <Eyebrow>Kryvo Resources</Eyebrow>
                            <h1 className="mt-5 max-w-2xl font-display text-4xl font-extrabold tracking-tight text-[#111827] sm:text-5xl">
                                The Growth Playbook
                            </h1>
                            <p className="mt-4 max-w-xl text-base leading-relaxed text-[#64748B] sm:text-lg">
                                Practical CRM thinking for growing sales teams — process,
                                follow-up discipline and engagement, minus the jargon.
                            </p>
                        </Reveal>
                    </Container>
                </section>

                <section className="pb-20 lg:pb-28">
                    <Container>
                        {loading && (
                            <p className="py-16 text-center text-sm text-[#94A3B8]">Loading articles…</p>
                        )}
                        {!loading && featured && (
                            <>
                                <Reveal>
                                    <Link
                                        to={`/blog/${featured.slug}`}
                                        data-testid="blog-featured-card"
                                        className="group block overflow-hidden rounded-3xl border border-[#E5E7EB] bg-gradient-to-br from-[#F0FDF4] to-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl sm:p-10"
                                    >
                                        <div className="flex flex-wrap items-center gap-3">
                                            <span className="rounded-full bg-[#16A34A] px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                                                Featured
                                            </span>
                                            <span className="rounded-full border border-[#16A34A]/20 bg-white px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-[#15803D]">
                                                {featured.category}
                                            </span>
                                        </div>
                                        <h2 className="mt-5 max-w-2xl font-display text-2xl font-extrabold leading-snug tracking-tight text-[#111827] transition-colors group-hover:text-[#15803D] sm:text-3xl">
                                            {featured.title}
                                        </h2>
                                        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#64748B]">
                                            {featured.excerpt}
                                        </p>
                                        <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                                            <Meta post={featured} />
                                            <span className="flex items-center gap-1.5 text-sm font-semibold text-[#16A34A]">
                                                Read article <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                            </span>
                                        </div>
                                    </Link>
                                </Reveal>

                                <div className="mt-8 grid gap-5 md:grid-cols-3">
                                    {rest.map((p, i) => (
                                        <Reveal key={p.slug} delay={i * 0.07}>
                                            <Link
                                                to={`/blog/${p.slug}`}
                                                data-testid={`blog-card-${p.slug}`}
                                                className="group flex h-full flex-col rounded-2xl border border-[#E5E7EB] bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:border-[#16A34A]/30 hover:shadow-lg"
                                            >
                                                <div className="flex items-center justify-between">
                                                    <span className="rounded-full bg-[#F0FDF4] px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-[#15803D]">
                                                        {p.category}
                                                    </span>
                                                    <ArrowUpRight className="h-4 w-4 text-[#CBD5E1] transition-colors group-hover:text-[#16A34A]" />
                                                </div>
                                                <h3 className="mt-4 font-display text-lg font-bold leading-snug text-[#111827] transition-colors group-hover:text-[#15803D]">
                                                    {p.title}
                                                </h3>
                                                <p className="mt-2 flex-1 text-sm leading-relaxed text-[#64748B]">
                                                    {p.excerpt}
                                                </p>
                                                <div className="mt-5 border-t border-[#F1F5F9] pt-4">
                                                    <Meta post={p} />
                                                </div>
                                            </Link>
                                        </Reveal>
                                    ))}
                                </div>
                            </>
                        )}
                        {!loading && posts.length === 0 && (
                            <p className="py-16 text-center text-sm text-[#94A3B8]">Articles are on their way. Check back soon.</p>
                        )}
                    </Container>
                </section>
            </main>
            <Footer />
        </div>
    );
}
